import { useModuleBEM } from "@b2bportal/core-themes";
import {
  type WalletCreditTransaction,
  WalletCreditTransactionEnum,
} from "@b2bportal/hsbc-wallet-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import dayjs from "dayjs";
import styles from "./styles.module.scss";

type CreditHistorySummaryItemProps = WalletCreditTransaction;

enum TransactionType {
  Debit = "debit",
  Credit = "credit",
}

type ProgramListItemData = {
  type: TransactionType;
  program?: string;
  earnPending?: boolean;
  descriptionKey: string;
  expiryDate?: string;
  transactionDate: string;
};

const formatDate = (date: string) => {
  return dayjs(date).format("DD/MM/YYYY");
};

const keyPrefix = "wallet.credits.transaction";
const getProgramData = (
  transaction: WalletCreditTransaction
): ProgramListItemData => {
  const { WalletCreditTransaction: transactionType } = transaction;
  switch (transactionType) {
    case WalletCreditTransactionEnum.Earn:
      return {
        type: TransactionType.Debit,
        program: transaction.program,
        earnPending: transaction.isPending,
        descriptionKey: `${keyPrefix}.earn`,
        transactionDate: transaction.earnedOn,
        expiryDate: transaction.expiresOn,
      };
    case WalletCreditTransactionEnum.Refund:
      return {
        type: TransactionType.Debit,
        descriptionKey: `${keyPrefix}.refund`,
        transactionDate: transaction.refundedOn,
      };
    case WalletCreditTransactionEnum.Redemption:
      return {
        type: TransactionType.Credit,
        descriptionKey: `${keyPrefix}.redeem`,
        transactionDate: transaction.redeemedOn,
      };
    case WalletCreditTransactionEnum.Expiration:
      return {
        type: TransactionType.Credit,
        descriptionKey: `${keyPrefix}.expire`,
        transactionDate: transaction.expiredOn,
      };
    case WalletCreditTransactionEnum.Cancellation:
      return {
        type: TransactionType.Credit,
        descriptionKey: `${keyPrefix}.cancel`,
        transactionDate: transaction.cancelledOn,
      };
  }
};

export const CreditHistorySummaryItem: React.FC<
  CreditHistorySummaryItemProps
> = (props) => {
  const { t, formatFiatCurrency } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "CreditHistorySummaryItem");

  const { amount } = props;
  const {
    type,
    descriptionKey,
    program,
    transactionDate,
    expiryDate,
    earnPending,
  } = getProgramData(props);

  return (
    <div className={block}>
      <span className={cn("date")}>{formatDate(transactionDate)}</span>

      <span className={cn("program")}>{program || t(descriptionKey)}</span>
      <span className={cn("amount", { [type]: true })}>
        {type === TransactionType.Debit ? "+ " : "- "}
        {formatFiatCurrency(amount)}
      </span>

      {!!(expiryDate || earnPending) && (
        <span className={cn("expiry-date")}>
          {earnPending
            ? t(`${keyPrefix}.rewardsPending`)
            : t(`${keyPrefix}.expirationDate`, {
                date: formatDate(expiryDate),
                interpolation: { escapeValue: false },
              })}
        </span>
      )}
    </div>
  );
};
